import { blue, ChevronDefaultDown16Px, ChevronDefaultRight16Px, red } from '@galaxy-react/core';
import { useUnmount } from 'ahooks';
import { t } from 'i18next';
import { CSSProperties, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Dropdown from 'src/Shared/Components/Dropdown/Dropdown';
import { useAppDispatch } from 'src/app/hooks';
import { useGetAutoCompleteOptionListQuery } from 'src/app/standaloneSlices/apiSlice';
import { getFilterValueByName, upsertFilterAction, validateAction } from 'src/feature/Filters/FiltersSlice';
import {
  getFilterModelByName,
  getFilterNameString,
  IComponentProps,
  IFilter,
  ISelectWithActiveFlag,
} from '../filterModels';

export default function SingleSelector({
  filterName,
  isTopLevel = true,
  passedInFilterModel,
  disabled,
}: IComponentProps) {
  const filter: IFilter = useMemo(
    () => passedInFilterModel || getFilterModelByName(filterName),
    [filterName, passedInFilterModel],
  );
  const filterData = useSelector(getFilterValueByName(filterName));

  const dispatch = useAppDispatch();
  const { data, isLoading, isError, refetch } = useGetAutoCompleteOptionListQuery(filter?.fetchFilterDataAPI || '', {
    skip: filter?.config?.isStatic || !filter?.fetchFilterDataAPI,
  });

  const originalOptions: ISelectWithActiveFlag[] = useMemo(() => {
    if (filter?.config?.isStatic) {
      return [...filter?.staticDropDownOptionsList!];
    }

    let list: ISelectWithActiveFlag[] = [];
    let processedData = data;
    if (filter?.preprocessFilterDataFromServer && data !== undefined) {
      processedData = filter?.preprocessFilterDataFromServer(data);
    }
    if (Array.isArray(processedData)) {
      list = processedData ? processedData?.map(filter.dropDownOptionTransform) : [];
    } else {
      let objectArray = processedData ? processedData[Object.keys(processedData)[0]] : [];
      list = objectArray ? objectArray?.map(filter.dropDownOptionTransform) : [];
    }
    const formattedOptions = list.concat(filter?.staticDropDownOptionsList ? filter?.staticDropDownOptionsList : []);
    return formattedOptions;
  }, [data, filter]);
  const updateFilterValue = useCallback(
    (value) => {
      dispatch(
        upsertFilterAction({
          name: getFilterNameString(filterName),
          data: value,
        }),
      );
    },
    [dispatch, filterName],
  );

  useEffect(() => {
    if (!isLoading) {
      if (originalOptions.length) {
        const defaultOption =
          originalOptions?.find((i) => i.isDefault) ||
          originalOptions.find((i) => i.value === filter.defaultValue) ||
          originalOptions?.[0];
        updateFilterValue(defaultOption?.value);
      } else {
        updateFilterValue(null);
        dispatch(validateAction());
      }
    }
  }, [dispatch, filter.defaultValue, isLoading, originalOptions, updateFilterValue]);

  const onOptionSelect = (data: { optionValue?: string; selectedOptions: string[] }) => {
    updateFilterValue(data.optionValue);
  };
  useUnmount(() => {
    updateFilterValue(null);
  });

  useEffect(() => {
    if (isError) {
      updateFilterValue(null);
    }
  }, [isError, updateFilterValue]);

  const displayValue = useMemo(() => {
    const selectedLabel = originalOptions.find((item) => item.value === filterData?.data)?.label;
    let text = isError ? 'Reload' : isLoading ? 'Loading...' : selectedLabel;
    if (isError) {
      text = t('Reload');
    } else if (isLoading) {
      text = t('Loading...');
    } else if (!originalOptions?.length) {
      text = filter?.noOptionsPlaceholder || t('No options');
    } else {
      text = selectedLabel;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
        <span style={{ marginRight: 8 }}>{filter.displayName}:</span>
        <span>{text}</span>
      </div>
    );
  }, [originalOptions, isError, isLoading, filter.displayName, filter?.noOptionsPlaceholder, filterData?.data]);

  const style: CSSProperties = useMemo(() => {
    if (!!filterData?.error || isError) {
      if (isTopLevel) {
        return {
          width: '100%',
          borderColor: red[500],
          color: red[500],
        };
      } else {
        return {
          width: '100%',
          color: red[500],
        };
      }
    } else if (!isTopLevel) {
      return {
        width: '100%',
        borderColor: 'transparent',
      };
    } else {
      return {
        width: '100%',
      };
    }
  }, [filterData?.error, isTopLevel, isError]);

  return (
    <Dropdown<string>
      appearance={isTopLevel ? undefined : 'transparent'}
      style={style}
      value={displayValue}
      options={originalOptions}
      selectedOptions={[filterData?.data]}
      onChange={onOptionSelect}
      disabled={isLoading || disabled}
      isTopLevel={isTopLevel}
      positioning={isTopLevel ? 'below-start' : 'after-top'}
      menuIcon={
        isTopLevel ? (
          <ChevronDefaultDown16Px color={!!filterData?.error || isError ? red[500] : blue[700]} />
        ) : (
          <ChevronDefaultRight16Px color={!!filterData?.error || isError ? red[500] : blue[700]} />
        )
      }
      onClick={() => {
        if (isError) {
          refetch();
        }
      }}
    />
  );
}
